import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import { type DateWindow, dateWindowSchema } from './dateWindow'
import { AttemptsPerMeeting, OverallEvidence } from './EvidenceTables'
import FeedbackError from './FeedbackError'
import FeedbackFilters from './FeedbackFilters'
import ReportRatings from './ReportRatings'
import ReportsTable from './ReportsTable'
import { usePrereadRatingStats, usePrereadReports, usePrereadStats } from './usePrereadFeedback'

import styles from './PrereadFeedback.module.scss'

import { isApiError } from '@/api'
import type { EvidenceScope, EvidenceStats, PrereadFilters, PrereadRatingBucket } from '@/api/prereadFeedback'
import Button from '@/components/Button'
import { type CountLine, CountTrend } from '@/components/RatingTrend'
import Tabs from '@/components/Tabs'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'
import { DAY_MS, utcDay } from '@/utils/dates'

const defaultDates = () => {
  const today = Date.parse(`${utcDay(Date.now())}T00:00:00Z`)
  return { since: utcDay(today - 6 * DAY_MS), until: utcDay(today + DAY_MS) }
}
const OUTCOME_LINES: CountLine<EvidenceStats['buckets'][number]>[] = [
  { key: 'failed', label: 'Failed', color: 'var(--trend-failure-color)', value: (row) => row.failedGenerations },
  { key: 'empty', label: 'Empty', color: 'var(--trend-empty-color)', value: (row) => row.emptyGenerations },
]
const denied = (error: unknown) => isApiError(error) && error.status === 403

const PrereadFeedbackIndexPage = () => {
  const [params, setParams] = useSearchParams()
  const [defaults] = useState(defaultDates)
  const isAdmin = useSelector(selectIsInternalAdmin)
  const since = params.get('since') ?? defaults.since
  const until = params.get('until') ?? defaults.until
  const source = params.get('source')
  const rating = params.get('rating')
  const model = params.get('model')
  const isUsage = params.get('view') === 'usage'
  const scope: EvidenceScope = params.get('scope') === 'reported' ? 'reported' : 'all'
  const cursor = params.get('cursor') ?? undefined
  const bucket: PrereadRatingBucket = params.get('bucket') === 'day' ? 'day' : 'week'
  const hasValidWindow = dateWindowSchema.safeParse({ since, until }).success
  const filters: PrereadFilters = {
    since: hasValidWindow ? `${since}T00:00:00Z` : '',
    until: hasValidWindow ? `${until}T00:00:00Z` : '',
    ...(source === 'internal' || source === 'external' ? { source } : {}),
    ...(rating === 'up' || rating === 'down' ? { rating } : {}),
    ...(model === 'luna' || model === 'claude' ? { model } : {}),
  }
  const reportFilters = { ...filters, matchedOnly: true }
  const reports = usePrereadReports(reportFilters, cursor, isAdmin && hasValidWindow && !isUsage)
  const ratings = usePrereadRatingStats(reportFilters, bucket, isAdmin && hasValidWindow && !isUsage)
  const stats = usePrereadStats(filters, scope, bucket, isAdmin && hasValidWindow && isUsage)
  const isDenied = denied(reports.error) || denied(stats.error) || denied(ratings.error)
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'bucket') next.delete('cursor')
    setParams(next)
  }
  const applyDates = (dates: DateWindow) => {
    const next = new URLSearchParams(params)
    next.set('since', dates.since)
    next.set('until', dates.until)
    next.delete('cursor')
    setParams(next)
  }
  const nextCursor = reports.data?.nextCursor

  return (
    <main className={styles.page}>
      <Helmet>
        <title>Preread feedback · Wispr Flow</title>
      </Helmet>
      <div className={styles.content}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Internal tools</p>
          <h1>Preread feedback</h1>
          <p className={styles.subtitle}>
            Review internal and external reports, and inspect the evidence behind meeting briefs.
          </p>
        </header>
        {!isAdmin || isDenied ? (
          <p role="alert">Internal admin access is required.</p>
        ) : (
          <>
            <div className={styles.tabs}>
              <Tabs
                tabs={['Reported briefs', 'Overall tool usage']}
                activeTab={isUsage ? 1 : 0}
                setActiveTab={(tab) => update('view', tab === 1 ? 'usage' : '')}
              />
            </div>
            <FeedbackFilters
              since={since}
              until={until}
              source={filters.source}
              rating={filters.rating}
              model={filters.model}
              scope={scope}
              isUsage={isUsage}
              isRefreshDisabled={!hasValidWindow || reports.isFetching || ratings.isFetching || stats.isFetching}
              onChange={update}
              onApplyDates={applyDates}
              onRefresh={() => {
                if (isUsage) void stats.refetch()
                else {
                  void reports.refetch()
                  void ratings.refetch()
                }
              }}
            />
            {!hasValidWindow ? (
              <p role="alert" className={styles.notice}>
                Choose a valid date window of up to 90 days. “Before” excludes that date.
              </p>
            ) : (
              <>
                {isUsage ? (
                  <>
                    <p className={styles.caption}>
                      {scope === 'all'
                        ? 'Generation attempts within the selected dates. Source selects the owner cohort; a rating filter includes only generations with matching reports.'
                        : 'Distinct matched generations from feedback reported within the selected dates. Their generation date may be earlier.'}
                    </p>
                    {stats.isError ? (
                      <FeedbackError onRetry={() => void stats.refetch()} />
                    ) : stats.isPending ? (
                      <p role="status">Loading tool usage…</p>
                    ) : (
                      <OverallEvidence stats={stats.data}>
                        {scope === 'all' && (
                          <>
                            <CountTrend
                              title="Failed and empty briefs over time"
                              description="generation attempts in current filters"
                              since={filters.since}
                              until={filters.until}
                              bucket={bucket}
                              onBucket={(value) => update('bucket', value)}
                              buckets={stats.data.buckets}
                              lines={OUTCOME_LINES}
                              emptyMessage="No failed or empty briefs in this range."
                            />
                            <AttemptsPerMeeting rows={stats.data.attemptsPerMeeting} />
                          </>
                        )}
                      </OverallEvidence>
                    )}
                  </>
                ) : (
                  <>
                    <ReportRatings
                      since={filters.since}
                      until={filters.until}
                      bucket={bucket}
                      stats={ratings.data}
                      isError={ratings.isError}
                      onBucket={(value) => update('bucket', value)}
                      onRetry={() => void ratings.refetch()}
                    />
                    <p className={styles.caption}>
                      Exact-match feedback reports from both channels, newest first. Counts and rates include all
                      reports in the current filters.
                    </p>
                    {reports.isError ? (
                      <FeedbackError onRetry={() => void reports.refetch()} />
                    ) : (
                      <ReportsTable reports={reports.data?.items ?? []} isLoading={reports.isPending} />
                    )}
                    <div className={styles.pagination}>
                      <span>{reports.data ? `${reports.data.total.toLocaleString()} reports` : ''}</span>
                      <div>
                        <Button
                          title="First page"
                          type="light"
                          isDisabled={!cursor || reports.isFetching}
                          onClick={() => update('cursor', '')}
                        />
                        <Button
                          title="Next page"
                          type="light"
                          isDisabled={!nextCursor || reports.isFetching || reports.isError}
                          onClick={() => {
                            if (nextCursor) setParams({ ...Object.fromEntries(params), cursor: nextCursor })
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </main>
  )
}

export default PrereadFeedbackIndexPage
